/*
 * Deskon - homepage hero: cap the slideshow at two slides, drop empty ones, and recreate the CTA
 * if the hero has lost its own.
 *
 * WHY ANY OF THIS IS IN JAVASCRIPT. All of it belongs in the theme's own files - the slide count
 * and the empty-slot check in sections/hero.liquid, the button in templates/index.json. Neither
 * can be edited safely: hero.liquid is 68 KB and index.json 87 KB, and the Theme API only offers
 * whole-file writes on files it will not return in one piece, so any change would be an
 * unverifiable rewrite of the homepage. These are the smallest interventions that get the same
 * result on screen.
 *
 * ---------------------------------------------------------------------------------------------
 * WHAT A "SLIDE" ACTUALLY IS HERE - and why two earlier attempts silently did nothing
 * ---------------------------------------------------------------------------------------------
 * There are no slide elements. sections/hero.liquid builds three captures - media_slot_1,
 * media_slot_2, media_slot_3 - each producing a bare <picture> (or <img>, or a video), and drops
 * all three straight into <hero-slideshow class="hero__slideshow">. So the slides are simply that
 * element's direct children.
 *
 * Earlier versions of this file looked for "slideshow-slide", ".hero__slide" and similar. None of
 * those exist in this theme, so querySelector returned nothing, the functions returned early, and
 * the grey placeholder kept rotating with no error anywhere to explain why. Reading the section
 * file was what settled it - the structure is not guessable from the rendered page alone.
 *
 * ---------------------------------------------------------------------------------------------
 * THE THIRD SLIDE IS REMOVED
 * ---------------------------------------------------------------------------------------------
 * MAX_SLIDES caps the hero at two, at every screen size. The "Media 3" fields still appear in the
 * theme editor - removing a setting means editing the section file - but nothing put in them will
 * render. To bring the third slide back, raise MAX_SLIDES to 3; to allow any number, delete the
 * limitSlides call in run().
 *
 * EMPTY SLIDES are removed too. A slot with no image renders Shopify's grey placeholder graphic
 * and the slideshow rotates to it, so the hero goes blank for one interval. They are removed from
 * the DOM rather than hidden with CSS: display: none would take the picture away but the
 * slideshow would still count the slide and still hold on its position. The blank interval is the
 * problem, not the grey.
 *
 * Both run before the slideshow's own script. Module scripts execute in document order after the
 * DOM is parsed, and this file is loaded from the <head> via snippets/stylesheets.liquid while the
 * slideshow's is loaded inside the section - so the slides are already gone when it counts them,
 * and its pagination comes out right without being told.
 *
 * ---------------------------------------------------------------------------------------------
 * THE CTA
 * ---------------------------------------------------------------------------------------------
 * If the promo column has no link or button of its own, one is created. It checks first: an
 * earlier version injected unconditionally and produced two buttons side by side, the theme's own
 * promo_cta plus this one. The stylesheet gives both the same terracotta treatment, so whichever
 * exists looks identical. A real Button block added in the editor takes precedence and stays
 * editable there, which is the better arrangement.
 */

const MAX_SLIDES = 2;

const CTA_LABEL = 'Set the mood';
const CTA_LINK = '/collections/all';

const EYEBROW_NEW_TEXT = 'Deskon';
const EYEBROW_OLD_TEXT = 'New collection · Spring 2026';

const HERO = '.hero-split-wrapper';
const SLIDESHOW = 'hero-slideshow, .hero__slideshow';
const PROMO_CONTENT = '.hero-split-wrapper__promo .color-scheme-deskon2-ink > .group-block-content';
const HERO_COPY = '.hero-split-wrapper .hero__content-wrapper';
const CTA_CLASS = 'deskon-hero-cta';

/* Shopify's placeholder_svg_tag always emits .placeholder-svg, whatever extra classes the theme
 * passes it - here it passes 'hero__image' - so that is the reliable marker for an empty slot. */
const PLACEHOLDER = '.placeholder-svg';

/** The slides: the direct children of the slideshow element. */
function getSlides() {
  const container = document.querySelector(SLIDESHOW);
  if (!container) return [];

  return Array.from(container.children);
}

function limitSlides() {
  const slides = getSlides();
  if (slides.length <= MAX_SLIDES) return;

  slides.slice(MAX_SLIDES).forEach((slide) => slide.remove());
}

function pruneEmptySlides() {
  getSlides().forEach((slide) => {
    /* The placeholder may be the child itself - placeholder_svg_tag returns the <svg> - or sit
     * inside a <picture>, depending on which media type the empty slot is set to. */
    if (slide.matches(PLACEHOLDER) || slide.querySelector(PLACEHOLDER)) slide.remove();
  });
}

function injectCta() {
  const host = document.querySelector(PROMO_CONTENT);
  if (!host) return;
  if (host.querySelector('a, button')) return;

  const cta = document.createElement('a');
  cta.className = CTA_CLASS;
  cta.href = CTA_LINK;
  cta.textContent = CTA_LABEL;
  host.appendChild(cta);
}

/* Only rewritten while it still holds the old default. Type anything else into that field in the
 * editor and this leaves it alone from then on - it will not fight you. */
function fixEyebrow() {
  const copy = document.querySelector(HERO_COPY);
  if (!copy) return;

  const eyebrow = copy.querySelector('p');
  if (!eyebrow) return;
  if (eyebrow.textContent.trim() !== EYEBROW_OLD_TEXT) return;

  eyebrow.textContent = EYEBROW_NEW_TEXT;
}

function run() {
  limitSlides();
  pruneEmptySlides();
  injectCta();
  fixEyebrow();
}

run();

/* The theme editor replaces a section's markup wholesale when a setting changes, discarding
 * anything done here - so re-run on the events Shopify fires afterwards. */
document.addEventListener('shopify:section:load', run);
document.addEventListener('shopify:block:select', run);
document.addEventListener('shopify:block:deselect', run);
